/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { LuisSplitPane } from 'src/core/components/LuisSplitPane';
import { ShimmerFactory } from 'src/core/components/ShimmerFactory';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSplitPane } from 'src/core/hooks/useSplitPane';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { watchResize } from 'src/core/utils/resizeWatcher';
import {
    SidePaneEntitySelection,
    SidePaneEntitySelectionStore
} from 'src/luis/appShell/buildPane/intentPage/stores/SidePaneEntitySelectionStore';
import { useModels } from 'src/luis/modules/models/useModels';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { LabelingMode } from 'src/luis/projects/utterances/stores/LabelingStore';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';
import { AdvancedOptionsPane } from 'src/luis/verticals/labelingSidePane/components/AdvancedOptionsPane';
import { EntityTreeContainer } from 'src/luis/verticals/labelingSidePane/components/EntityTreeContainer';
import { advancedOptionsPaneHeaderHeight, entityTreeHeaderHeight } from 'src/luis/verticals/labelingSidePane/constants';

const { styleComponent, styleDiv } = getStylistV2('IntentLabelingSidePane');

const Root = styleDiv('Root', {
    height: '100%',
    position: 'relative',
    overflow: 'hidden'
});

const Pane = styleComponent<IStackProps>(Stack)('Pane', {
    overflow: 'hidden',
    width: '100%',
    height: '100%'
});

const Loading = styleComponent<IStackProps>(Stack)('Loading', {
    padding: 12
});

/**
 * Filter predicate for eligible entities for labeling.
 * Role-based entities (Prebuilt & Regex) that have a role.
 * Simple entities.
 * List entities.
 * @param entityStore Entity to check eligibility for.
 */
const isEntityEligibleForLabeling = (entityStore: EntityStore) => {
    const isRoleBasedEntity = entityStore.typeId === ModelType.Regex || entityStore.typeId === ModelType.Prebuilt;

    return (
        (isRoleBasedEntity && entityStore.entityRoleStoreHouse.items.length) ||
        entityStore.typeId === ModelType.Simple ||
        entityStore.typeId === ModelType.List ||
        entityStore.typeId === ModelType.Composite ||
        entityStore.typeId === ModelType.Hierarchical
    );
};

/**
 * Maps selection ids to actual stores.
 * @param entityStores List of all the entity stores.
 * @param entitySelectionStore Contains id of selected stores.
 */
const prepareEntityStores = (entityStores: readonly EntityStore[], entitySelectionStore: SidePaneEntitySelectionStore) => {
    const eligibleEntityStores = entityStores.filter(isEntityEligibleForLabeling);

    const rootEntityStore = entitySelectionStore.selection.parentId
        ? eligibleEntityStores.find(e => e.id === entitySelectionStore.selection.parentId)
        : null;

    const selectedEntityStore =
        rootEntityStore &&
        rootEntityStore.typeId === ModelType.Simple &&
        entitySelectionStore.selection.parentId !== entitySelectionStore.selection.entityId
            ? (rootEntityStore as SimpleEntityStore).getChildById(entitySelectionStore.selection.entityId)
            : eligibleEntityStores.find(e => e.id === (entitySelectionStore.selection.parentId || entitySelectionStore.selection.entityId));

    const selectedRoleStore = selectedEntityStore
        ? selectedEntityStore.entityRoleStoreHouse.findById(entitySelectionStore.selection?.roleId)
        : null;

    const selectedHierarchicalChildId =
        entitySelectionStore.selection.entityId === entitySelectionStore.selection.parentId
            ? null
            : entitySelectionStore.selection.entityId;

    return { eligibleEntityStores, rootEntityStore, selectedEntityStore, selectedRoleStore, selectedHierarchicalChildId };
};

type IntentLabelingSidePaneProps = {
    /**
     * Determines the mode of the labeling.
     */
    labelingMode: LabelingMode;
    /**
     * Optional className for styling the component.
     */
    className?: string;
    /**
     * Selection object that holds what entity is selected for labeling utterances.
     */
    entitySelectionStore?: SidePaneEntitySelectionStore;
    /**
     * Callback for updating the labeling selection.
     */
    onChangeSelection?: (selection: SidePaneEntitySelection) => void;
    /**
     * Callback for updating if labeling is enabled.
     */
    onToggleLabeling: (on: boolean) => void;
    /**
     * Callback to show create new entity modal.
     */
    onStartCreateEntity: () => void;
};

/**
 * Renders the intent page labeling side pane.
 * It shows an entity tree that represents the entities on can label utterances with.
 */
export const IntentLabelingSidePane = observer((props: IntentLabelingSidePaneProps) => {
    const { className, onToggleLabeling, entitySelectionStore, onChangeSelection, onStartCreateEntity, labelingMode } = props;

    const { dispatcher } = useGlobalState();
    const { entityStores, isLoading } = useModels();
    const rootElementRef = React.useRef<HTMLElement>();

    const { size: height, setSize: setHeight, maxSize: maxHeight, setMaxSize: setMaxHeight } = useSplitPane({
        defaultSize: '50%',
        maxSize: '50%'
    });

    const recalculateSizeAllowance = (totalHeight: number) => {
        const newSidePaneMaxSize = totalHeight - entityTreeHeaderHeight;
        if (maxHeight !== newSidePaneMaxSize) {
            setMaxHeight(newSidePaneMaxSize);
        }
    };

    React.useEffect(() => {
        const watcher = watchResize(rootElementRef.current, dim => {
            recalculateSizeAllowance(dim.height);
        });

        return () => watcher.unobserve();
    });

    const createRoleProgressTracker = useProgressTracker({
        onComplete: (roleId: string) => {
            onChangeSelection({ parentId: null, normalizedValue: null, entityId: selectedEntityStore.id, roleId });
        }
    });

    const createNormalizedValueProgressTracker = useProgressTracker({
        onComplete: (normalizedValue: string) => {
            onChangeSelection({ parentId: null, normalizedValue, entityId: selectedEntityStore.id, roleId: null });
        }
    });

    const {
        eligibleEntityStores,
        rootEntityStore,
        selectedEntityStore,
        selectedHierarchicalChildId,
        selectedRoleStore
    } = prepareEntityStores(entityStores, entitySelectionStore);

    const createRole = React.useCallback((entity: Entity, roleName: string) => {
        dispatcher.dispatch('addEntityRole', { entityId: entity.id, roleName, tracker: createRoleProgressTracker });
    }, []);

    const selectRole = React.useCallback((entity: Entity, roleId: LuisId) => {
        onChangeSelection({ ...entitySelectionStore.selection, parentId: null, entityId: entity.id, roleId });
    }, []);

    const createNormalizedValue = React.useCallback((entity: Entity, normalizedValue: string) => {
        dispatcher.dispatch('addSublistToListEntity', {
            id: entity.id,
            sublist: { name: normalizedValue, values: [] },
            tracker: createNormalizedValueProgressTracker
        });
    }, []);

    const selectNormalizedValue = React.useCallback((entity: Entity, normalizedValue: string) => {
        onChangeSelection({ ...entitySelectionStore.selection, parentId: null, normalizedValue, entityId: entity.id });
    }, []);

    const selectHierarchicalChildEntity = React.useCallback((entity: Entity, childEntityId: LuisId) => {
        // If child entity is null, select entity itself
        onChangeSelection({ entityId: childEntityId || entity.id, parentId: entity.id, normalizedValue: null, roleId: null });
    }, []);

    const selectEntity = React.useCallback(({ entityId, parentId }: { parentId: LuisId; entityId: LuisId }) => {
        onChangeSelection({ parentId, entityId, normalizedValue: null, roleId: null });
    }, []);

    return (
        <Root className={className} originalRef={rootElementRef} role="complementary">
            <LuisSplitPane
                split="horizontal"
                defaultSize="50%"
                minSize={advancedOptionsPaneHeaderHeight}
                maxSize={maxHeight}
                size={height}
                primary="second"
                onChange={setHeight}
                onResizerDoubleClick={() => setHeight('50%')}>
                <Pane>
                    {isLoading ? (
                        <Loading>
                            <ShimmerFactory shimmerCount={12} shimmerSpacing={15} fullWidth />
                        </Loading>
                    ) : (
                        <EntityTreeContainer
                            entityStores={eligibleEntityStores}
                            isLabelingOn={labelingMode === 'brush'}
                            onToggleLabeling={onToggleLabeling}
                            selectedEntityStore={selectedEntityStore}
                            onSelectEntity={selectEntity}
                            onStartCreateEntity={onStartCreateEntity}
                        />
                    )}
                </Pane>
                <Pane>
                    <AdvancedOptionsPane
                        isProgressing={createRoleProgressTracker.isRunning || createNormalizedValueProgressTracker.isRunning}
                        rootEntityStore={rootEntityStore}
                        entityStore={selectedEntityStore}
                        selectedRole={selectedRoleStore}
                        selectedNormalizedValue={entitySelectionStore.selection.normalizedValue}
                        onSelectRole={selectRole}
                        onCreateRole={createRole}
                        onSelectNormalizedValue={selectNormalizedValue}
                        onCreateNormalizedValue={createNormalizedValue}
                        onSelectHierarchicalChildEntity={selectHierarchicalChildEntity}
                        selectedHierarchicalChildId={selectedHierarchicalChildId}
                    />
                </Pane>
            </LuisSplitPane>
        </Root>
    );
});
